import { Button, Card, Typography } from "@codat/orchard-ui";
import { UserClientInfo } from "api/clients";
import { useCreateClientSelectLog } from "api/logs";
import classNames from "classnames";
import { format as dateFormat, parseISO } from "date-fns";
import React from "react";
import Cookies from "universal-cookie";
import { sanitizeRedirect } from "utils/sanitizeRedirect";

import FlexContainer from "components/FlexContainer";
import config from "config";

import styles from "./ClientCard.module.css";

const cookies = new Cookies();

const contactSupportText = (client: UserClientInfo) => {
  return (
    "mailto:support@codat.io?subject=Client Expired&body=Hi support, %0D%0AMy client " +
    client.clientName +
    " has expired." +
    "%0D%0ACan I please get some assistance to extend my trial?" +
    "%0D%0A" +
    "The client id is: " +
    client.clientId +
    " and it expired on " +
    dateFormat(parseISO(client.expiredDate as string), "dd/MM/yyyy") +
    ". %0D%0A" +
    "Thank you"
  );
};

export const ClientCard: React.FC<{ client: UserClientInfo }> = ({
  client,
}) => {
  const [{ loading: clientSelectLoading }, createClientSelectLog] =
    useCreateClientSelectLog();

  const getSubtitle = () => {
    if (!client.clientLoginProviderEnabled) {
      return "Client disabled via login provider";
    }
    if (client.expired && client.expiredDate) {
      return (
        "Expired: " + dateFormat(parseISO(client.expiredDate), "dd/MM/yyyy")
      );
    } else {
      return client.clientRole;
    }
  };

  const setClient = () => {
    cookies.set(config.APP_CLIENT_COOKIE_NAME, client.clientId, {
      path: "/",
      domain: ".codat.io",
    });
    createClientSelectLog({
      data: { clientId: client.clientId, userId: client.userId },
    });

    const urlSearchParams = new URLSearchParams(window.location.search);

    // Only redirect to URL if it is on a Codat domain, otherwise redirect to portal
    const redirectUrl =
      sanitizeRedirect(urlSearchParams.get("redirectUrl")) ?? config.PORTAL_URL;

    if (redirectUrl) {
      window.location.assign(redirectUrl);
    }
  };

  const getButton = () => {
    if (!client.clientLoginProviderEnabled) {
      return (
        <Button
          isLoading={clientSelectLoading}
          label="Select"
          variant="secondary"
          data-testid={"client-select-btn-" + client.clientId}
          disabled
        />
      );
    }

    if (client.expired && client.expiredDate) {
      return (
        <a
          className={styles.contact}
          href={contactSupportText(client)}
          data-testid={"client-contact-support-" + client.clientId}
        >
          Contact Support
        </a>
      );
    }

    return (
      <Button
        label="Select"
        variant="secondary"
        data-testid={"client-select-btn-" + client.clientId}
        onClick={setClient}
      />
    );
  };

  const isFreeAccount = client?.clientType === "ClientFreeTrial";
  const isTestAccount =
    client?.clientType === "ClientTest" ||
    client?.clientType === "ClientProspect" ||
    client?.clientType === "ClientTestStartup";

  const getClientTypeText = () => {
    if (!isFreeAccount && !isTestAccount) {
      return null;
    }

    return (
      <Typography className={styles.clientType} variant="small" as="p">
        {isFreeAccount ? "Free plan" : "Test account"}
      </Typography>
    );
  };

  return (
    <Card size="small" className={styles.clientCard}>
      <FlexContainer
        justifyContent="space-between"
        alignItems="center"
        testId={"client-select-container-" + client.clientId}
      >
        <div className={classNames({ [styles.expiredText]: client.expired })}>
          <Typography
            variant="heading_xl"
            as="h2"
            data-testid={"client-select-name-" + client.clientName}
            className={styles.clientName}
          >
            {client.clientName}
          </Typography>
          <div className={styles.clientType}>{getClientTypeText()}</div>

          <Typography
            data-testid={"client-select-subtitle-" + client.clientId}
            variant="small"
            as="p"
            className={styles.userType}
          >
            {getSubtitle()}
          </Typography>
        </div>

        {getButton()}
      </FlexContainer>
    </Card>
  );
};
